import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import { buildBoneOnlyOutlinerHierarchy } from '../../../lib/outliner';
import { getChainCssColor } from '../../../presentations/chain';
import {
  ExcludedBoneIcon,
  PickEndBoneIcon,
  PickStartBoneIcon,
} from '../../../presentations/icons';

import {
  CHAIN_MIN_COLUMN_WIDTH,
  EXCLUDED_MIN_COLUMN_WIDTH,
  ROLE_MIN_COLUMN_WIDTH,
  ROW_COLUMN_GAP_PX,
  ROW_HORIZONTAL_PADDING_TOTAL,
} from '../constants';
import type { OutlinerProps } from '../types';
import { useViewerHoveredBoneName } from '../../BoneAssignments/viewerBoneHoverEvent';
import {
  buildDefaultExpanded,
  buildFullyExpanded,
  buildHasVisibleDescendantMap,
  buildLookups,
  buildRowGridTemplateColumns,
  buildVisibleRows,
  measureVisibleTreeColumnWidth,
} from '../utils';
import BoneRow from './BoneRow';

const CURSOR_HINT_WIDTH_PX = 168;
const CURSOR_HINT_MAX_WIDTH_PX = 420;
const CURSOR_HINT_CHAR_WIDTH_PX = 7.4;
const HOVERED_BONE_NAME_MAX_WIDTH_PX = 220;
const CURSOR_HINT_HEIGHT_PX = 36;
const CURSOR_HINT_OFFSET_PX = 8;
const CURSOR_HINT_EDGE_PADDING_PX = 8;
const TITLE_LABEL_WIDTH_BUFFER_PX = 2;

type CompactTitleState = {
  bone: boolean;
  role: boolean;
  chain: boolean;
};

const DEFAULT_COMPACT_TITLE_STATE: CompactTitleState = {
  bone: false,
  role: false,
  chain: false,
};

const EMPTY_MAPPING_IO_EXTRAS: Record<string, unknown> = {};

function getCursorHintWidthPx(
  baseWidthPx: number,
  hoveredBoneName: string | null,
) {
  if (!hoveredBoneName) return baseWidthPx;

  const hoveredNameWidthPx = Math.min(
    HOVERED_BONE_NAME_MAX_WIDTH_PX,
    Math.ceil(hoveredBoneName.length * CURSOR_HINT_CHAR_WIDTH_PX) + 12,
  );
  return Math.min(CURSOR_HINT_MAX_WIDTH_PX, baseWidthPx + hoveredNameWidthPx);
}

function shouldCollapseTitleLabel(
  container: HTMLElement | null,
  label: string,
): boolean {
  if (!container || typeof window === 'undefined') return false;
  const context = document.createElement('canvas').getContext('2d');
  if (!context) return false;
  const styles = window.getComputedStyle(container);
  context.font = `${styles.fontStyle} ${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
  const labelWidth = Math.ceil(context.measureText(label).width);
  return labelWidth + TITLE_LABEL_WIDTH_BUFFER_PX > container.clientWidth;
}

export default function Outliner({
  skeleton,
  hierarchy = null,
  mapping,
  assignedBoneIndexSet,
  selectedNodeId = null,
  selectedBoneIndex,
  selectionVersion,
  excludedBoneRefs,
  hideExcludedBones = false,
  hideUnassignedBones = false,
  hideTransformNodes = false,
  showExcludedColumn = false,
  showRolesColumn = true,
  showChainColumn = true,
  showTitleRow = true,
  autoRevealSelectedBone = true,
  propertiesVersion = 0,
  treeAction = null,
  onToggleBoneExcluded,
  onOverrideBoneRole,
  onClearBoneChain,
  onSetChainFromStartEnd,
  onAddBoneToChain,
  onRemoveBoneFromChain,
  onMinContentWidthChange,
  onSelectNode,
  onHoverChange,
  onViewerPickModeChange,
}: OutlinerProps) {
  const resolvedHierarchy = useMemo(
    () => hierarchy ?? buildBoneOnlyOutlinerHierarchy(skeleton),
    [hierarchy, skeleton],
  );
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    buildDefaultExpanded(resolvedHierarchy, 6),
  );
  const [pendingChainPathSelection, setPendingChainPathSelection] = useState<{
    chainName: string;
    phase: 'pick-start' | 'pick-end';
    startBoneRef: string | null;
    lastHandledBoneIndex: number | null;
    lastHandledSelectionVersion: number;
  } | null>(null);
  const [cursorHintPosition, setCursorHintPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const hoveredViewerBoneName = useViewerHoveredBoneName();
  const cursorHintWidthPx = getCursorHintWidthPx(
    CURSOR_HINT_WIDTH_PX,
    hoveredViewerBoneName,
  );
  const titleRowRef = useRef<HTMLDivElement>(null);
  const boneTitleCellRef = useRef<HTMLDivElement>(null);
  const roleTitleCellRef = useRef<HTMLDivElement>(null);
  const chainTitleCellRef = useRef<HTMLDivElement>(null);
  const outlinerScrollContainerRef = useRef<HTMLDivElement>(null);
  const revealScrollFrameRef = useRef<number | null>(null);
  const [compactTitleState, setCompactTitleState] = useState<CompactTitleState>(
    DEFAULT_COMPACT_TITLE_STATE,
  );
  const lastHandledTreeActionTokenRef = useRef(0);
  const lastAutoRevealedSelectionRef = useRef<{
    nodeId: string;
    selectionVersion: number;
  } | null>(null);

  const lookups = useMemo(
    () => buildLookups(mapping, skeleton),
    [mapping, skeleton],
  );
  const presentRoleNames = useMemo(
    () => new Set(Object.keys(mapping?.roleToBone ?? {})),
    [mapping],
  );
  const presentChainNames = useMemo(
    () => new Set(Object.keys(mapping?.chains ?? {})),
    [mapping],
  );
  const excludedSet = useMemo(
    () =>
      new Set<number>(
        excludedBoneRefs
          .map((ref) => skeleton.resolveBoneRef(ref, { namespace: 'internal' }))
          .filter((idx): idx is number => idx !== null),
      ),
    [excludedBoneRefs, skeleton],
  );
  const hasVisibleDescendants = useMemo(
    () =>
      buildHasVisibleDescendantMap(
        resolvedHierarchy,
        excludedSet,
        hideExcludedBones,
        hideTransformNodes,
        hideUnassignedBones,
        assignedBoneIndexSet,
      ),
    [
      assignedBoneIndexSet,
      excludedSet,
      hideExcludedBones,
      hideTransformNodes,
      hideUnassignedBones,
      resolvedHierarchy,
    ],
  );
  const visibleRows = useMemo(
    () =>
      buildVisibleRows(
        resolvedHierarchy,
        expanded,
        excludedSet,
        hideExcludedBones,
        hideTransformNodes,
        hideUnassignedBones,
        assignedBoneIndexSet,
      ),
    [
      assignedBoneIndexSet,
      excludedSet,
      expanded,
      hideExcludedBones,
      hideTransformNodes,
      hideUnassignedBones,
      resolvedHierarchy,
    ],
  );
  const selectedBoneNodeId = useMemo(
    () =>
      selectedBoneIndex !== null
        ? (resolvedHierarchy.boneNodeIdByIndex[selectedBoneIndex] ?? null)
        : null,
    [resolvedHierarchy, selectedBoneIndex],
  );
  const selectedAncestorNodeIds = useMemo(() => {
    const activeSelectedNodeId = selectedNodeId ?? selectedBoneNodeId;
    if (!activeSelectedNodeId) return new Set<string>();

    const ancestors = new Set<string>();
    let currentParentId =
      resolvedHierarchy.nodesById[activeSelectedNodeId]?.parentId ?? null;
    while (currentParentId) {
      ancestors.add(currentParentId);
      currentParentId = resolvedHierarchy.nodesById[currentParentId]?.parentId ?? null;
    }
    return ancestors;
  }, [resolvedHierarchy, selectedBoneNodeId, selectedNodeId]);
  const mappingIoExtras = mapping?.ioExtras ?? EMPTY_MAPPING_IO_EXTRAS;

  const treeColumnWidth = useMemo(() => {
    // Property changes mutate bones in place; version forces width recalculation.
    void propertiesVersion;
    return measureVisibleTreeColumnWidth(
      visibleRows,
      skeleton,
      mappingIoExtras,
    );
  }, [mappingIoExtras, propertiesVersion, skeleton, visibleRows]);
  const rowGridTemplateColumns = useMemo(
    () =>
      buildRowGridTemplateColumns(
        treeColumnWidth,
        showExcludedColumn,
        showRolesColumn,
        showChainColumn,
      ),
    [showChainColumn, showExcludedColumn, showRolesColumn, treeColumnWidth],
  );
  const visibleColumnCount =
    1 +
    (showExcludedColumn ? 1 : 0) +
    (showRolesColumn ? 1 : 0) +
    (showChainColumn ? 1 : 0);
  const minContentWidth = useMemo(
    () =>
      treeColumnWidth +
      (showExcludedColumn ? EXCLUDED_MIN_COLUMN_WIDTH : 0) +
      (showRolesColumn ? ROLE_MIN_COLUMN_WIDTH : 0) +
      (showChainColumn ? CHAIN_MIN_COLUMN_WIDTH : 0) +
      ROW_HORIZONTAL_PADDING_TOTAL +
      ROW_COLUMN_GAP_PX * Math.max(0, visibleColumnCount - 1),
    [
      showChainColumn,
      showExcludedColumn,
      showRolesColumn,
      treeColumnWidth,
      visibleColumnCount,
    ],
  );

  const toggleExpanded = useCallback(
    (nodeId: string, includeDescendants: boolean) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        const shouldExpand = !next.has(nodeId);
        if (!includeDescendants) {
          if (shouldExpand) next.add(nodeId);
          else next.delete(nodeId);
          return next;
        }

        const nodeIdsToVisit = [nodeId];
        while (nodeIdsToVisit.length > 0) {
          const currentNodeId = nodeIdsToVisit.pop();
          if (!currentNodeId) continue;
          const currentNode = resolvedHierarchy.nodesById[currentNodeId];
          if (!currentNode) continue;
          if (shouldExpand && currentNode.childrenIds.length > 0) {
            next.add(currentNodeId);
          } else if (!shouldExpand) {
            next.delete(currentNodeId);
          }
          for (const childId of currentNode.childrenIds) {
            nodeIdsToVisit.push(childId);
          }
        }

        return next;
      });
    },
    [resolvedHierarchy],
  );
  const revealNodeInOutliner = useCallback(
    (nodeId: string | null) => {
      if (!nodeId) return;

      setExpanded((prev) => {
        const next = new Set(prev);
        let didChange = false;
        let currentParentId = resolvedHierarchy.nodesById[nodeId]?.parentId ?? null;

        while (currentParentId) {
          if (!next.has(currentParentId)) {
            next.add(currentParentId);
            didChange = true;
          }
          currentParentId = resolvedHierarchy.nodesById[currentParentId]?.parentId ?? null;
        }

        return didChange ? next : prev;
      });
      if (typeof window === 'undefined') return;
      if (revealScrollFrameRef.current !== null) {
        window.cancelAnimationFrame(revealScrollFrameRef.current);
      }
      revealScrollFrameRef.current = window.requestAnimationFrame(() => {
        const container = outlinerScrollContainerRef.current;
        if (!container) {
          revealScrollFrameRef.current = null;
          return;
        }
        const rows = container.querySelectorAll<HTMLElement>('[data-outliner-node-id]');
        const targetRow = [...rows].find(
          (element) => element.dataset.outlinerNodeId === nodeId,
        );
        targetRow?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        revealScrollFrameRef.current = null;
      });
    },
    [resolvedHierarchy],
  );

  const treeActionToken = treeAction?.token ?? 0;
  const treeActionType = treeAction?.type ?? null;

  useEffect(() => {
    setExpanded(buildDefaultExpanded(resolvedHierarchy, 6));
  }, [resolvedHierarchy]);

  useEffect(() => {
    if (!treeActionType || treeActionToken <= 0) return;
    if (treeActionToken === lastHandledTreeActionTokenRef.current) return;
    lastHandledTreeActionTokenRef.current = treeActionToken;
    if (treeActionType === 'expand-all') {
      setExpanded(buildFullyExpanded(resolvedHierarchy));
      return;
    }
    if (treeActionType === 'collapse-all') {
      setExpanded(new Set<string>());
      return;
    }

    revealNodeInOutliner(selectedBoneNodeId ?? selectedNodeId);
  }, [
    revealNodeInOutliner,
    selectedBoneNodeId,
    selectedNodeId,
    treeActionToken,
    treeActionType,
  ]);

  useEffect(() => {
    if (autoRevealSelectedBone) return;
    lastAutoRevealedSelectionRef.current = null;
  }, [autoRevealSelectedBone]);

  useEffect(() => {
    if (!autoRevealSelectedBone || !selectedBoneNodeId) return;

    if (
      lastAutoRevealedSelectionRef.current?.selectionVersion === selectionVersion &&
      lastAutoRevealedSelectionRef.current.nodeId === selectedBoneNodeId
    ) {
      return;
    }

    lastAutoRevealedSelectionRef.current = {
      nodeId: selectedBoneNodeId,
      selectionVersion,
    };
    revealNodeInOutliner(selectedBoneNodeId);
  }, [
    autoRevealSelectedBone,
    revealNodeInOutliner,
    selectedBoneNodeId,
    selectionVersion,
  ]);

  useEffect(
    () => () => {
      if (typeof window === 'undefined') return;
      if (revealScrollFrameRef.current === null) return;
      window.cancelAnimationFrame(revealScrollFrameRef.current);
    },
    [],
  );

  useEffect(
    () => () => {
      onViewerPickModeChange?.(null);
    },
    [onViewerPickModeChange],
  );

  useEffect(() => {
    return () => onHoverChange?.(false);
  }, [onHoverChange]);

  useEffect(() => {
    onMinContentWidthChange?.(minContentWidth);
  }, [minContentWidth, onMinContentWidthChange]);

  useEffect(() => {
    if (!mapping) {
      setPendingChainPathSelection(null);
    }
  }, [mapping]);

  useEffect(() => {
    if (!pendingChainPathSelection) {
      setCursorHintPosition(null);
    }
  }, [pendingChainPathSelection]);

  useEffect(() => {
    if (!onViewerPickModeChange) return;
    if (!pendingChainPathSelection) {
      onViewerPickModeChange(null);
      return;
    }
    onViewerPickModeChange({
      kind: 'chain',
      chainName: pendingChainPathSelection.chainName,
      phase: pendingChainPathSelection.phase,
    });
  }, [onViewerPickModeChange, pendingChainPathSelection]);

  useEffect(() => {
    if (!pendingChainPathSelection || !onSetChainFromStartEnd) return;
    if (selectedBoneIndex === null) return;
    if (
      selectedBoneIndex === pendingChainPathSelection.lastHandledBoneIndex &&
      selectionVersion === pendingChainPathSelection.lastHandledSelectionVersion
    ) {
      return;
    }

    const selectedBoneRef = skeleton.refForBoneIndex(selectedBoneIndex);
    if (pendingChainPathSelection.phase === 'pick-start') {
      setPendingChainPathSelection((prev) => {
        if (!prev) return prev;
        if (prev.phase !== 'pick-start') return prev;
        return {
          ...prev,
          phase: 'pick-end',
          startBoneRef: selectedBoneRef,
          lastHandledBoneIndex: selectedBoneIndex,
          lastHandledSelectionVersion: selectionVersion,
        };
      });
      return;
    }

    if (!pendingChainPathSelection.startBoneRef) return;
    onSetChainFromStartEnd(
      pendingChainPathSelection.chainName,
      pendingChainPathSelection.startBoneRef,
      selectedBoneRef,
    );
    setPendingChainPathSelection(null);
  }, [
    onSetChainFromStartEnd,
    pendingChainPathSelection,
    selectedBoneIndex,
    selectionVersion,
    skeleton,
  ]);

  useEffect(() => {
    if (!pendingChainPathSelection) return;

    const handleMouseMove = (event: MouseEvent) => {
      const viewerArea = document.querySelector(
        '[data-cartographer-viewer-area="true"]',
      );
      if (!(viewerArea instanceof HTMLElement)) return;

      const rect = viewerArea.getBoundingClientRect();
      const isWithinViewerArea =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!isWithinViewerArea) {
        setCursorHintPosition(null);
        return;
      }

      const minX = rect.left + CURSOR_HINT_EDGE_PADDING_PX;
      const minY = rect.top + CURSOR_HINT_EDGE_PADDING_PX;
      const maxX = Math.max(
        minX,
        rect.right - cursorHintWidthPx - CURSOR_HINT_EDGE_PADDING_PX,
      );
      const maxY = Math.max(
        minY,
        rect.bottom - CURSOR_HINT_HEIGHT_PX - CURSOR_HINT_EDGE_PADDING_PX,
      );
      const nextX = event.clientX + CURSOR_HINT_OFFSET_PX;
      const nextY = event.clientY + CURSOR_HINT_OFFSET_PX;

      setCursorHintPosition({
        x: Math.min(maxX, Math.max(minX, nextX)),
        y: Math.min(maxY, Math.max(minY, nextY)),
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [cursorHintWidthPx, pendingChainPathSelection]);

  const recomputeCompactTitleState = useCallback(() => {
    if (!showTitleRow) {
      setCompactTitleState(DEFAULT_COMPACT_TITLE_STATE);
      return;
    }

    const nextCompactState: CompactTitleState = {
      bone: shouldCollapseTitleLabel(boneTitleCellRef.current, 'Bone'),
      role: showRolesColumn
        ? shouldCollapseTitleLabel(roleTitleCellRef.current, 'Role')
        : false,
      chain: showChainColumn
        ? shouldCollapseTitleLabel(chainTitleCellRef.current, 'Chain')
        : false,
    };

    setCompactTitleState((previousState) =>
      previousState.bone === nextCompactState.bone &&
      previousState.role === nextCompactState.role &&
      previousState.chain === nextCompactState.chain
        ? previousState
        : nextCompactState,
    );
  }, [showChainColumn, showRolesColumn, showTitleRow]);

  useEffect(() => {
    recomputeCompactTitleState();
  }, [recomputeCompactTitleState, rowGridTemplateColumns]);

  useEffect(() => {
    if (!showTitleRow || typeof ResizeObserver === 'undefined') return;

    const titleRowElement = titleRowRef.current;
    if (!titleRowElement) return;

    const observer = new ResizeObserver(() => {
      recomputeCompactTitleState();
    });
    observer.observe(titleRowElement);

    return () => observer.disconnect();
  }, [recomputeCompactTitleState, showTitleRow]);

  const cursorHint =
    pendingChainPathSelection &&
    cursorHintPosition &&
    typeof document !== 'undefined'
      ? createPortal(
          <div
            className="pointer-events-none fixed z-[120]"
            style={{
              left: `${cursorHintPosition.x}px`,
              top: `${cursorHintPosition.y}px`,
            }}
          >
            <div
              className="text-white inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full px-3 py-1.5 shadow-sm"
              style={{
                backgroundColor: getChainCssColor(pendingChainPathSelection.chainName),
                maxWidth: `${cursorHintWidthPx}px`,
              }}
            >
              {pendingChainPathSelection.phase === 'pick-end' && (
                <PickEndBoneIcon className="h-4 w-4 shrink-0" />
              )}
              <Text
                variant="studio/body/medium/sm"
                className="shrink-0 text-white"
              >
                {pendingChainPathSelection.phase === 'pick-start'
                  ? 'Pick Start Bone'
                  : 'Pick End Bone'}
              </Text>
              {pendingChainPathSelection.phase === 'pick-start' && (
                <PickStartBoneIcon className="h-4 w-4 shrink-0" />
              )}
              {hoveredViewerBoneName && (
                <Text
                  variant="studio/body/medium/sm"
                  className="min-w-0 truncate text-white"
                >
                  {hoveredViewerBoneName}
                </Text>
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={outlinerScrollContainerRef}
      className="translucent-white-scrollbar relative h-full select-none overflow-y-auto rounded-xl"
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        onSelectNode(null, null);
      }}
    >
      {cursorHint}
      {showTitleRow && (
        <div
          ref={titleRowRef}
          className="grid items-center gap-x-2 px-1.5 pb-1 pt-0.5"
          style={{
            gridTemplateColumns: rowGridTemplateColumns,
          }}
        >
          <div
            ref={boneTitleCellRef}
            className="flex min-w-0 items-center justify-center"
          >
            <Text
              as="span"
              variant="studio/body/medium/xs"
              className="block min-w-0 truncate text-center"
            >
              {compactTitleState.bone ? 'B' : 'Bone'}
            </Text>
          </div>
          {showExcludedColumn && (
            <div className="flex min-w-0 items-center justify-center">
              <Tooltip text="Excluded" side="top" inModal>
                <span
                  className="text-tertiary inline-flex h-5 w-5 items-center justify-center rounded-md"
                  aria-label="Excluded"
                >
                  <ExcludedBoneIcon className="h-3.5 w-3.5" />
                </span>
              </Tooltip>
            </div>
          )}
          {showRolesColumn && (
            <div
              ref={roleTitleCellRef}
              className="flex min-w-0 items-center justify-center"
            >
              <Text
                as="span"
                variant="studio/body/medium/xs"
                className="block min-w-0 truncate text-center"
              >
                {compactTitleState.role ? 'R' : 'Role'}
              </Text>
            </div>
          )}
          {showChainColumn && (
            <div
              ref={chainTitleCellRef}
              className="flex min-w-0 items-center justify-center"
            >
              <Text
                as="span"
                variant="studio/body/medium/xs"
                className="block min-w-0 truncate text-center"
              >
                {compactTitleState.chain ? 'C' : 'Chain'}
              </Text>
            </div>
          )}
        </div>
      )}
      {visibleRows.map(({ node, depth }) => (
        <BoneRow
          key={node.id}
          node={node}
          bone={node.kind === 'bone' ? skeleton.bone(node.boneIndex) : null}
          lookups={lookups}
          presentRoleNames={presentRoleNames}
          presentChainNames={presentChainNames}
          excludedSet={excludedSet}
          selectedNodeId={selectedNodeId}
          selectedBoneIndex={selectedBoneIndex}
          isAncestorOfSelected={selectedAncestorNodeIds.has(node.id)}
          expanded={expanded}
          hasVisibleDescendants={hasVisibleDescendants.get(node.id) ?? false}
          depth={depth}
          rowGridTemplateColumns={rowGridTemplateColumns}
          showExcludedColumn={showExcludedColumn}
          showRolesColumn={showRolesColumn}
          showChainColumn={showChainColumn}
          onSelectNode={onSelectNode}
          onToggleExpanded={toggleExpanded}
          onToggleBoneExcluded={onToggleBoneExcluded}
          onOverrideBoneRole={mapping ? onOverrideBoneRole : undefined}
          onClearBoneChain={mapping ? onClearBoneChain : undefined}
          onBeginChainPathSelection={
            mapping && onSetChainFromStartEnd
              ? (chainName) =>
                  setPendingChainPathSelection({
                    chainName,
                    phase: 'pick-start',
                    startBoneRef: null,
                    lastHandledBoneIndex: selectedBoneIndex,
                    lastHandledSelectionVersion: selectionVersion,
                  })
              : undefined
          }
          onCancelChainPathSelection={
            pendingChainPathSelection
              ? () => setPendingChainPathSelection(null)
              : undefined
          }
          onAddBoneToChain={mapping ? onAddBoneToChain : undefined}
          onRemoveBoneFromChain={mapping ? onRemoveBoneFromChain : undefined}
          pendingChainPathSelection={pendingChainPathSelection}
          mappingIoExtras={mappingIoExtras}
        />
      ))}
    </div>
  );
}
